import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, BookOpen, Play, FileText, Zap } from 'lucide-react';
import { getRelatedLinks } from '../data/relatedLinks';

/**
 * <RelatedLinks> — Bloc éditorial premium de maillage interne.
 *
 * Objectif SEO :
 *  - Augmenter le crawl interne et le temps de session
 *  - Renforcer l'autorité thématique en signalant à Google
 *    quels contenus sont liés sémantiquement.
 *
 * Props :
 *  - topic : clé de `RELATED_LINKS_MAP` (ex. "casque", "plateau", "etm")
 *  - title : sur-titre du bloc (par défaut "À lire ensuite")
 *  - kicker : kicker éditorial (par défaut "Pour aller plus loin")
 *  - max : nombre maximum de liens à afficher (par défaut 5)
 *  - className
 *
 * Usage : `<RelatedLinks topic="plateau" />`
 */

const INTENT_ICON = {
  reviser: Target,
  comprendre: BookOpen,
  pratiquer: Play,
  reference: FileText,
  video: Zap,
};

const INTENT_COLOR = {
  reviser: '#cf2031',
  comprendre: '#0891b2',
  pratiquer: '#f59e0b',
  reference: '#7c3aed',
  video: '#16a34a',
};

export default function RelatedLinks({
  topic,
  title = 'À lire ensuite',
  kicker = 'Pour aller plus loin',
  max = 5,
  className = '',
}) {
  const links = getRelatedLinks(topic).slice(0, max);
  if (!links.length) return null;

  return (
    <aside
      className={`rounded-2xl bg-card border border-border/40 p-5 sm:p-7 relative overflow-hidden ${className}`}
      data-testid={`related-links-${topic}`}
      aria-label="Liens éditoriaux pour aller plus loin"
    >
      <span className="absolute top-0 inset-x-0 h-[3px] bg-[hsl(var(--primary))]" aria-hidden="true" />

      <div className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-2">
        {kicker}
      </div>
      <h2
        className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight leading-tight"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {title}.
      </h2>

      <ul className="mt-5 grid sm:grid-cols-2 gap-2.5 sm:gap-3" role="list">
        {links.map((link, i) => {
          const Icon = INTENT_ICON[link.intent] || BookOpen;
          const color = INTENT_COLOR[link.intent] || '#cf2031';
          const isExternalRoute = link.path?.startsWith('http');
          const Tag = isExternalRoute ? 'a' : Link;
          const tagProps = isExternalRoute
            ? { href: link.path, target: '_blank', rel: 'noopener noreferrer' }
            : { to: link.path };

          return (
            <li key={`${topic}-${i}-${link.path}`}>
              <Tag
                {...tagProps}
                className="group flex items-start gap-3 rounded-xl border border-border/40 hover:border-[hsl(var(--primary))]/40 bg-background/40 hover:bg-background/70 px-4 py-3 transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5"
                data-testid={`related-link-${topic}-${i}`}
              >
                <span
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: `${color}1f`, color }}
                  aria-hidden="true"
                >
                  <Icon className="w-4 h-4" />
                </span>
                <span className="flex-1 min-w-0">
                  {link.kicker && (
                    <span
                      className="block text-[9px] font-black uppercase tracking-[0.18em] mb-0.5"
                      style={{ color }}
                    >
                      {link.kicker}
                    </span>
                  )}
                  <span
                    className="block text-sm sm:text-base font-extrabold tracking-tight text-foreground leading-snug group-hover:text-[hsl(var(--primary))] transition-colors"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {link.label}
                  </span>
                </span>
                <ArrowRight
                  className="w-4 h-4 text-foreground/40 group-hover:text-[hsl(var(--primary))] group-hover:translate-x-0.5 transition-[color,transform] flex-shrink-0 mt-1"
                  aria-hidden="true"
                />
              </Tag>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
